<script setup>
// X91 (Grant 2026-10-21: "The times on the left have weird cramped cells by the side of them and look weird being in
// line with the horizontal lines. They can be normal cells (fixed and sticky when scrolling horizontally) and have the
// full time period in 24hr format e.g. 8:00-8:30, 14:00-15:00"): the time column of a booking table, shared by the
// member grid (BookingDayGrid, /book) and the admin calendar (BookingCalendar). One ordinary cell per slot row, as tall
// as the row (the grid's --bt-rh), between the same hairlines as the row (stronger on the hour), its period centred in
// it: "8:00–8:30", "9:40–10:20". The column is sticky on the left with a solid background, so the playing areas scroll
// under it; its width is the shared booking-time-width (_booking-table.scss). On a phone a period that doesn't fit
// breaks after its dash ("14:00–" / "15:00"). The default slot draws over the cells (the admin calendar's changes of
// the day's hours); listeners fall through to the column itself.
//
//   <booking-time-column :rows="[{key, period: '8:00–8:30', start?: '8:00', hour: true, style?}]"/>
// Grant 2026-11-03: the All table's scale rows have their own heights (minutes × px per minute), passed as `style`.
// QA 2026-11-04: on a phone the cell shows the START only, on one line ("9:00"; the next row says the end), instead of
// "9:00–" / "9:30" crammed into a 30px row; `start` when given, else the period's first half.
defineProps({
  /** [{key, period, hour}]: one per slot row, in order. */
  rows: {type: Array, default: () => []},
});
// The period in two halves, so a narrow column breaks after the dash and nowhere else.
const halves = (period) => {
  const i = String(period ?? '').indexOf('–');
  return i < 0 ? [String(period ?? ''), ''] : [period.slice(0, i + 1), period.slice(i + 1)];
};
const startOf = (r) => r.start ?? halves(r.period)[0].replace(/–$/, '');
</script>

<template>
  <div class="btc">
    <div v-for="r in rows" :key="r.key" class="btc-cell" :class="{'btc-cell--hour': r.hour}" :style="r.style">
      <span class="btc-a">{{ halves(r.period)[0] }}</span><span class="btc-b">{{ halves(r.period)[1] }}</span><span class="btc-s">{{ startOf(r) }}</span>
    </div>
    <slot/>
  </div>
</template>

<style scoped lang="scss">
/* Sticky on the left inside the grid's sideways scroller, with a background so the cells scroll under it. */
.btc {
  position: sticky;
  left: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface));
  border-right: 1px solid rgb(var(--v-theme-borderLight));
}

/* An ordinary cell: the row's height, the row's hairline on top, the period centred in it (never on a line). */
.btc-cell {
  height: var(--bt-rh, 52px);
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  overflow: hidden;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 11px;
  font-weight: 600;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
  /* Over whatever the grid draws in the column (the admin calendar's changes), and see-through to presses. */
  position: relative;
  z-index: 2;
  pointer-events: none;

  &.btc-cell--hour {
    border-top-color: #D4D4D4;
  }

  &:first-child {
    border-top-color: transparent;
  }
}

.btc-a,
.btc-b {
  display: inline-block;
  white-space: nowrap;
}

.btc-s {
  display: none;
  white-space: nowrap;
}

/* A phone: the start only, on one line, with the kit's small gutter. */
@media (max-width: 640px) {
  .btc-cell {
    padding: 0 4px;
    font-size: 11px;
  }

  .btc-a,
  .btc-b {
    display: none;
  }

  .btc-s {
    display: inline-block;
  }
}
</style>
